import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { TagModel } from '@/domain/model/customer'
import { QUERIES } from '@/enums/queries'
import { useUpdateURLSearchParams } from '@/hooks/useQuery'
import useSearchParams from '@/hooks/useSearchParams'
import { invalidLoadCustomerById } from '@/queries/customers/useLoadCustomer'
import { useLoadTagsClient } from '@/queries/customers/useLoadTagsClient'
import { useUpdateTagClientById } from '@/queries/customers/useUpdateTagClientById'
import { globalQueryClient } from '@/queries/queryClient'
import Modal from '@/ui/Modal'

import { ClientTagForm } from './ClientTagForm'
import { ClientTagModel, useClientTagForm } from './useClientTagForm'

export const EditTagClientModal = () => {
  const { clientId } = useParams<{ clientId: string }>()
  const searchParams = useSearchParams()
  const editTagId = searchParams.get('editTag') || ''
  const { data: badgeData = [] } = useLoadTagsClient()

  const updateParams = useUpdateURLSearchParams()
  const { form, rule } = useClientTagForm()

  const { updateTagClientById, isLoading } = useUpdateTagClientById()

  const currentTag = badgeData.find((item) => String(item.id) === editTagId)

  const onFormSubmit = async (data: ClientTagModel) => {
    try {
      globalQueryClient.setQueryData<TagModel[]>(
        [QUERIES.CUSTOMERS.TAGS.LIST],
        (old) => {
          if (!old) return old
          return old.map((item) => {
            if (String(item.id) !== editTagId) {
              return item
            }
            return {
              ...item,
              ...data,
            }
          })
        }
      )
      await updateTagClientById({ ...data, id: editTagId })
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      invalidLoadCustomerById(clientId)
      toast.success('Tag edited successfully')
      updateParams(['editTag', undefined])
    } catch (error) {
      toast.error('Error while edit a tag')
    }
  }

  return createPortal(
    <Modal
      open
      cancelButtonProps={{ disabled: isLoading }}
      okButtonProps={{
        disabled: isLoading,
        loading: isLoading,
      }}
      onCancel={() => updateParams(['editTag', undefined])}
      onClose={() => updateParams(['editTag', undefined])}
      onOk={() => form.submit()}
      title="Edit Tag"
      width={{
        xs: '90%',
        sm: '50%',
        md: '50%',
        lg: '50%',
        xl: '30%',
        xxl: '30%',
      }}
    >
      <p className="mb-4 text-sm text-gray-500">
        Edit a specific tag name and color
      </p>

      <ClientTagForm
        {...{ form, onFormSubmit, rule, initialValues: currentTag }}
      />
    </Modal>,
    document.body
  )
}
